import { MainLayout } from "@/components/MainLayout";
import { SEO } from "@/components/SEO";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Building2,
  CreditCard,
  ArrowUpCircle,
  TrendingUp,
  FileText,
  Sparkles
} from "lucide-react";

const reportCategories = [
  {
    id: "categories",
    title: "Relatórios de Categorias",
    description: "Análise detalhada por categoria de despesas e receitas",
    icon: BarChart3,
    count: 8,
    color: "text-finance-blue",
    bgColor: "bg-finance-blue/10"
  },
  {
    id: "cost-centers",
    title: "Relatórios de Centros de Custo",
    description: "Análise por centro de custo e projetos",
    icon: Building2,
    count: 5,
    color: "text-finance-purple",
    bgColor: "bg-finance-purple/10"
  },
  {
    id: "payables",
    title: "Contas a Pagar",
    description: "Relatórios de despesas e obrigações a pagar",
    icon: CreditCard,
    count: 7,
    color: "text-finance-red",
    bgColor: "bg-finance-red/10"
  },
  {
    id: "receivables",
    title: "Contas a Receber",
    description: "Relatórios de receitas e valores a receber",
    icon: ArrowUpCircle,
    count: 11,
    color: "text-finance-green",
    bgColor: "bg-finance-green/10"
  },
  {
    id: "cash-flow",
    title: "Fluxo de Caixa",
    description: "Análise de entradas, saídas e saldo",
    icon: TrendingUp,
    count: 9,
    color: "text-finance-blue",
    bgColor: "bg-finance-blue/10"
  }
];

const customReportsCard = {
  id: "custom",
  title: "Relatórios Personalizados",
  description: "Crie seus próprios relatórios com filtros e visualizações personalizadas",
  icon: Sparkles,
  color: "text-blue-dark-500 dark:text-blue-dark-400",
  bgColor: "bg-blue-dark-50 dark:bg-blue-dark-950/30"
};

export default function ReportsPage() {
  const navigate = useNavigate();

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/reports/${categoryId}`);
  };

  const handleCustomReportsClick = () => {
    navigate('/reports/custom');
  };

  const CustomIcon = customReportsCard.icon;

  return (
    <MainLayout>
      <SEO
        title="Relatórios"
        description="Acesse relatórios financeiros detalhados, análises de gastos por categoria, comparações mensais e insights sobre sua situação financeira."
        keywords="relatórios financeiros, análise gastos, comparação mensal, insights financeiros"
      />

      <div className="space-y-6">
        <div className="section-delimited">
          <div>
            <h1 className="text-2xl font-bold mb-2">Relatórios Financeiros</h1>
            <p className="text-muted-foreground">
              Escolha uma categoria para visualizar relatórios detalhados
            </p>
          </div>
        </div>

        {/* Custom Reports Card - Featured */}
        <div className="section-delimited">
          <Card
            className="card-elevated cursor-pointer hover:shadow-lg transition-all duration-200 group border-blue-dark-200 dark:border-blue-dark-800/50 bg-gradient-to-br from-blue-dark-50/50 to-blue-dark-100/50 dark:from-blue-dark-950/20 dark:to-blue-dark-900/20"
            onClick={handleCustomReportsClick}
          >
            <CardHeader>
              <div className="flex items-start justify-between mb-2">
                <div className="p-3 rounded-lg bg-blue-dark-100 dark:bg-blue-dark-900/50 group-hover:scale-110 transition-transform">
                  <CustomIcon className="h-6 w-6 text-blue-dark-600 dark:text-blue-dark-400" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-dark-100 dark:bg-blue-dark-900/50 text-blue-dark-700 dark:text-blue-dark-300">
                    Novo
                  </span>
                </div>
              </div>
              <CardTitle className="group-hover:text-blue-dark-500 dark:group-hover:text-blue-dark-400 transition-colors">
                {customReportsCard.title}
              </CardTitle>
              <CardDescription>{customReportsCard.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span className="px-2 py-1 rounded bg-muted">Filtros personalizados</span>
                <span className="px-2 py-1 rounded bg-muted">Visualizações</span>
                <span className="px-2 py-1 rounded bg-muted">Exportação</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Standard Report Categories */}
        <div className="section-delimited">
          <h2 className="text-lg font-semibold mb-4 text-muted-foreground">Relatórios Predefinidos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reportCategories.map((category) => {
              const Icon = category.icon;
              return (
                <Card
                  key={category.id}
                  className="card-elevated cursor-pointer hover:shadow-lg transition-all duration-200 group"
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className={`p-3 rounded-lg ${category.bgColor}`}>
                        <Icon className={`h-6 w-6 ${category.color}`} />
                      </div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <FileText className="h-4 w-4" />
                        <span>{category.count} relatórios</span>
                      </div>
                    </div>
                    <CardTitle className="group-hover:text-primary transition-colors">
                      {category.title}
                    </CardTitle>
                    <CardDescription>{category.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
